import { Ref, ref, onMounted, onBeforeUnmount } from "vue";

type ImpressionCallback = (entry: IntersectionObserverEntry) => void;

interface ImpressionOptions extends IntersectionObserverInit {
    /** If true, disconnect after the first impression */
    once?: boolean;
}

/**
 * Composable to trigger a callback when an element enters the viewport
 */
export function useImpressionObserver(
    callback: ImpressionCallback,
    options: ImpressionOptions = { threshold: 0.5, once: true }
): { target: Ref<HTMLElement | null> } {
    const target = ref<HTMLElement | null>(null);
    let observer: IntersectionObserver | null = null;

    onMounted(() => {
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        callback(entry);

                        if (options.once) {
                            observer?.disconnect();
                        }
                    }
                });
            },
            options
        );

        if (target.value) {
            observer.observe(target.value);
        }
    });

    onBeforeUnmount(() => {
        if (observer) {
            observer.disconnect();
            observer = null;
        }
    });

    return { target };
}
